import type { ExtractableBlockData } from '@octopus-do/octopus-project-core'
import slugify from 'slugify'

import type { NodesWithBlocks } from '@/types'
import { wireframesName } from '../constants'

const getRoute = (page: NodesWithBlocks) => {
  if (page.url) {
    return `/${page.url}`
  }

  const slug = page.notes?.seoSlug

  if (slug) {
    return slug
  }

  const trimmedTitle = page.title.substring(0, 50)

  return `/${slugify(trimmedTitle || 'Page', {
    lower: true,
    strict: true,
  })}`
}

const getWireframes = (block: ExtractableBlockData) => {
  const { visual } = block

  if (visual.type !== 'wireframes') {
    return ''
  }

  const wireframes = visual.wireframes?.map(
    (wireframe) => wireframesName[wireframe] || wireframe
  )

  return wireframes.join(', ')
}

const hasBlockTemplateData = (block: ExtractableBlockData) => {
  return Boolean(block.title?.trim() || getWireframes(block))
}

const buildBlockTemplate = (block: ExtractableBlockData, index: number) => {
  const title = block.title || 'Untitled'
  const wireframes = getWireframes(block)
  const wireframesLine = wireframes ? `\n**Wireframes:** ${wireframes}` : ''

  return `#### Block ${index + 1}: ${title}${wireframesLine}`
}

const getPageTemplate = (
  title: string,
  route: string,
  seoTitle: string,
  seoDescription: string,
  blocksText: string
) => {
  return `### ${title}
**Route:** ${route}
**SEO Metadata:**
- **Title:** ${seoTitle}
- **Description:** ${seoDescription}

**Page Structure:**

${blocksText}`
}

const buildPageTemplate = (page: NodesWithBlocks) => {
  const blocksText = page.blocks
    .filter(hasBlockTemplateData)
    .map(buildBlockTemplate)
    .join('\n\n')
  const route = getRoute(page)
  const seoTitle = page.notes?.seoTitle ?? ''
  const seoDescription = page.notes?.seoDescription ?? ''

  return getPageTemplate(
    page.title,
    route,
    seoTitle,
    seoDescription,
    blocksText
  )
}

export const buildTemplate = (pages: NodesWithBlocks[]) => {
  return pages.map(buildPageTemplate).join('\n\n---\n\n')
}
